import { CS_RESERVED_CHARACTERS, INVOICE_STATUS_FILTER_MAP } from "../constants/constants";
import {
  hasMeaningfulFilterList,
  meaningfulFilterList,
} from "../components/common/Filter/filterUtils";
import { convertPANameToCode } from "./utils";

/** Only push CS filter clauses that have key, operation, and a real value. */
const pushValidFilter = (filterArray, filter) => {
  if (!filter?.key || !filter?.operation) return;
  const { value } = filter;
  if (value === null || value === undefined || value === "") return;
  if (Array.isArray(value) && value.length === 0) return;
  if (typeof value === "string" && value.trim() === "") return;
  filterArray.push(filter);
};

/**
 * Function to form generic search query
 * @param {*} searchText
 * @returns generic search query
 */
export const formRawSearchQuery = (searchText) => {
  if (searchText) {
    const refinedSearchText = getRefinedSearchText(searchText);
    if(refinedSearchText) {
      return { "query": `*${refinedSearchText}*` }
    }
    else {
      return {}
    }
  } else {
    return {}
  }
}

export const getRefinedSearchText = (searchText) => {
  let refinedSearchText = searchText;
  [...CS_RESERVED_CHARACTERS].forEach((item) => {
      refinedSearchText = refinedSearchText.split(item).join(`\\${item}`);
  })
  refinedSearchText = refinedSearchText.replace("<","");
  refinedSearchText = refinedSearchText.replace(">","");
  return refinedSearchText;
}
/**
 * Function  to form component specific filter objects
 * @param {*} key
 * @param {*} operation
 * @param {*} value
 * @returns
 */
export const formCsFilterObject = (key, operation, value) => {
  return {
    "key": key,
    "operation": operation,
    "value": value
  }
}

/**
 * Function to form filter sub-query
 * @param {*} filterValues
 * @returns filter query
 */
export const formCsFilterQuery = (practiceAreaNameToCode, filterValues) => {
  const filterArray = [];
  Object.keys(filterValues || {}).forEach(function (key) {
    // Skip null/undefined/[''] filter lists (column seed often leaves placeholders).
    if (hasMeaningfulFilterList(filterValues?.[key])) {
      const values = meaningfulFilterList(filterValues?.[key]);
      // Special condition for leadAttorney
      if(key === 'leadAttorney')
      {
        pushValidFilter(filterArray, {
          "key": "people.isLeadAttorney",
          "operation": "match",
          "value": true
        });
        const keyObj = `${key}-obj`
        pushValidFilter(filterArray, {
          "key": "people.id",
          "operation": "match",
          "value": filterValues?.[keyObj]?.referenceKey
        });
      }
      // special condition for isBudgetRequired
      else if ( key === "isBudgetRequired"){
        pushValidFilter(filterArray, {
          "key": "isBudgetRequired",
          "operation": "match",
          "value": values?.[0] === "Yes"
        });
      }
      // Special condition for practiceArea
      else if(key === "practiceArea")
      {
        const pAFilterArray = [];
        values?.forEach(function (item) {
          const paCode = convertPANameToCode(item, practiceAreaNameToCode)
          if (paCode) pAFilterArray.push(paCode)
        })
        pushValidFilter(filterArray, {
          "key": "practiceAreaCode",
          "operation": "in",
          "value": pAFilterArray
        });
      }
      // Default condition
      else if(!key?.includes('-obj'))
      {
        if(values.length === 1)
        {
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": values[0],
            "operation": "match"
          });
        }
        else
        {
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": values,
            "operation": "in"
          });
        }
      }
    }
  });
  return {
      "filters": filterArray,
      "operation": "AND",
      "properties": null
    }
}

/**
 * Function to form sort sub-query
 * @param {*} name
 * @param {*} direction
 * @returns sort query
 */
export const formCsSortQuery = (name, direction) => {
  return {
    "sortBy": [{
      "key": `${name}`,
      "type": `${direction}`
    }]
  }
}

/**
 * Function to form search sub-query
 * @param {*} searchText
 * @returns search query
 */
export const formCsSearchQuery = (searchText, COLUMNS=[]) => {
  let searchFilter;
  if(COLUMNS.length === 0) {
    searchFilter = [{
      "key": "*",
      "operation": "raw",
      "value": `*${searchText}*`
    }]
  } else {
    searchFilter = COLUMNS?.map((item) => {
      if (item?.colKey && item?.options?.display !== false) {
        return {
          "key": `${item?.colKey}`,
          "operation": "raw",
          "value": `*${searchText}*`
        }
      }
    })?.filter(function (item) {
      return item !== undefined;
    });
  }

    return {
      "filters": searchFilter,
      "operation": "OR",
      "properties":[{
         "filters":[{
            "key": "people.*",
            "operation": "raw",
            "value": `*${searchText}*`
          }],
         "operation": "AND"
        }]
     }
}

/**
 * Function to form pagination sub-query
 * @param {*} rowsPerPage
 * @param {*} page
 * @returns pagination query
 */
export const formCsPaginationQuery = (rowsPerPage, page) => {
  return {
    "offset": page,
    "limit": rowsPerPage,
  }
}

/**
 * Function to assemble various sub-queries and form the final composite search query
 * @param {*} scope
 * @param {*} filterQuery
 * @param {*} searchQuery
 * @param {*} sortQuery
 * @param {*} paginationQuery
 * @param {*} additionalQuery
 * @param  {...any} additionalProperties
 * @returns CS query object
 */
export const formCompositeSearchQuery = (scope, filterQuery, searchQuery, sortQuery, paginationQuery, additionalQuery, ...additionalProperties) => {
  const propertiesArray = [];
  // Check if the filterQuery is empty
  if((Object.keys(filterQuery).length !== 0) && (filterQuery.filters.length !== 0))
  {
  propertiesArray.push(filterQuery);
  }
  // Check if the additionalProperties is empty
  additionalProperties?.forEach((item) => {
  if((Object.keys(item).length !== 0) && (item?.filters?.length !== 0))
  {
  propertiesArray.push(item);
  }
  })
  // Check if the searchQuery is empty
  // if((Object.keys(searchQuery).length !== 0) && (searchQuery?.filters?.length !== 0))
  // {
  // propertiesArray.push(searchQuery);
  // }
  const finalFilterQuery = {
    "operation": "AND",
    "properties": propertiesArray,
    ...(searchQuery)
  }
  const query = {
    ...finalFilterQuery,
    ...sortQuery,
    ...paginationQuery,
    ...additionalQuery
  }
  // Map the query to all scopes
  let finalQuery = {};
  scope?.forEach((item) => {
    finalQuery = {
      [item]: query,
      ...finalQuery
    }
    })
  finalQuery = {
    "searchScopes": finalQuery
  }
  return finalQuery;
}


/**
 * Function to form search sub-query
 * @param {*} searchText
 * @param {optional} userType
 * @returns search query
 */
export const formPeopleCsSearchQuery = (searchText, COLUMNS=[]) => {
  let searchFilter;
  if(COLUMNS.length === 0) {
    searchFilter = [{
      "key": "*",
      "operation": "raw",
      "value": `*${searchText}*`
    }]
  } else {
    searchFilter = COLUMNS?.map((item) => {
      if (item?.colKey && item?.options?.display !== false) {
        return {
          "key": `${item?.colKey}`,
          "operation": "raw",
          "value": `*${searchText}*`
        }
      }
    })?.filter(function (item) {
      return item !== undefined;
    });
  }

  return {
    "filters": searchFilter,
    "operation": "OR",
  }
}

/**
 * Function to form filter sub-query
 * @param {*} filterValues
 * @returns filter query
 */
export const formPeopleCsFilterQuery = (filterValues) => {
  const filterArray = [];
  Object?.keys(filterValues || {})?.forEach(function (key) {
    // Skip null/undefined/[''] filter lists (column seed often leaves placeholders).
    if (hasMeaningfulFilterList(filterValues?.[key])) {
      const list = meaningfulFilterList(filterValues?.[key]);
      if(key === "startDate") {
        pushValidFilter(filterArray, {
          "key": "createdTs",
          "operation": "gte",
          "value": list?.[0] + " 00:00:00"
        });
        pushValidFilter(filterArray, {
          "key": "createdTs",
          "operation": "lte",
          "value": list?.[0] + " 23:59:59"
        });
      } else if (key === "createdTs") {
        pushValidFilter(filterArray, {
          "key": "createdTs",
          "operation": "gte",
          "value": list?.[0] + " 00:00:00"
        });
        pushValidFilter(filterArray, {
          "key": "createdTs",
          "operation": "lte",
          "value": list?.[1] + " 23:59:59"
        });
      } else if(key=== 'requestedRole') {
        let roleKey;
        let val = list[0]
        if (val === "Admin") {
          roleKey = "roleCodes"
          val="OC_ADMIN"
        } else {
          roleKey = "isTimeKeeperRoleEnabled"
          val = true
        }
        pushValidFilter(filterArray, {
          "key": `${roleKey}`,
          "value": val,
          "operation": "match"
        });
      } else if(key === "status"){
        let values = []
        list?.forEach((item)=>{
          INVOICE_STATUS_FILTER_MAP?.[item]?.forEach((status)=>{
            values.push(status)
          })
        })
        pushValidFilter(filterArray, {
          "key": `${key}`,
          "value": values,
          "operation": "in"
        });
      }
      else if(!key?.includes('-obj'))
      {
        if(list.length === 1)
        {
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": list?.[0],
            "operation": "match"
          });
        }
        else
        {
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": list,
            "operation": "in"
          });
        }
      }
    }
  });
  return {
    "filters": filterArray,
    "operation": "AND",
    "properties": null
  };
}

/**
 * Function to form search sub-query
 * @param {*} searchText
 * @returns search query
 */
export const formOrgSearchQuery = (searchText, COLUMNS=[]) => {
  let searchFilter;
  if(COLUMNS.length === 0) {
    searchFilter = [{
      "key": "*",
      "operation": "raw",
      "value": `*${searchText}*`
    }]
  } else {
    searchFilter = COLUMNS?.map((item) => {
      if (item?.colKey && item?.options?.display !== false) {
        return {
          "key": `${item?.colKey}`,
          "operation": "raw",
          "value": `*${searchText}*`
        }
      }
    })?.filter(function (item) {
      return item !== undefined;
    });
  }
  return {
    "filters": searchFilter,
    "operation": "OR",
  }
}

/**
   * Function to form search sub-query
   * @param {*} searchText
   * @param {optional} userType
   * @returns search query
   */
export const formBudgetCsSearchQuery = (searchText, COLUMNS) => {
  let searchColumns = COLUMNS?.map((item) => {
    if (item?.colKey && item?.options?.display !== false && item?.colKey !== "paidPercent") {
      if(item?.colKey === "status")
      {
        return {
          "key": `${item?.colKey}`,
          "operation": "raw",
          "value": `*${searchText.toUpperCase()}*`
        }
      }
      else{
      return {
        "key": `${item?.colKey}`,
        "operation": "raw",
        "value": `*${searchText}*`
      }
    }
    }
  })?.filter(function (item) {
    return item !== undefined;
  });
  if(searchColumns === undefined)
  {
    searchColumns = null;
  }
  return {
    "filters": searchColumns,
    "operation": "OR",
  }
}

/**
   * Function to form filter sub-query
   * @param {*} filterValues
   * @returns filter query
   */
export const formBudgetFilterQuery = (filterValues) => {
  const filterArray = [];
    Object.keys(filterValues || {}).forEach(function (key) {
      // Skip null/undefined/[''] filter lists (column seed often leaves placeholders).
      if (hasMeaningfulFilterList(filterValues?.[key]) && !key.includes("-obj")) {
        const list = meaningfulFilterList(filterValues?.[key]);
        if(list.length === 1 && key !== 'fiscalYear')
        {
          let value = list[0]
          if(key === "status"){
              value = list[0].toUpperCase()
          }
          if(key === "isActive") {
            value = list?.[0] === "Yes" || list?.[0] === true
          }
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": value,
            "operation": "match"
          });
        } else if (list.length && key === "fiscalYear") {
          let value = list[0]
          if (value === "LOM") {
            pushValidFilter(filterArray, {
              "key": "timePeriod",
              "value": "LIFE_OF_MATTER",
              "operation": "match"
            });
          } else if (value) {
            pushValidFilter(filterArray, {
              "key": "timePeriod",
              "value": "CUSTOM_DATE_RANGE",
              "operation": "match"
            });
            pushValidFilter(filterArray, {
              "key": "startDate",
              "value": `${value.slice(2, 6) - 1}-02-01` + " 00:00:00",
              "operation": "gte"
            });
            pushValidFilter(filterArray, {
              "key": "endDate",
              "value": `${value.slice(2, 6)}-01-31` + " 23:59:59",
              "operation": "lte"
            });
          }
        }
        else {
          const values = [...list]
          if (key === "status") {
            values.forEach((item, idx) => {
              values[idx] = item?.toUpperCase()
            })
          }
          pushValidFilter(filterArray, {
            "key": `${key}`,
            "value": values,
            "operation": "in"
          });
        }
      }
    });
    return {
      "filters": filterArray,
      "operation": "AND",
      "properties": null
    }
}

 /**
   * Function to form search sub-query for people auto-complete
   * @param {*} searchText
   * @returns search query
   */
 export const formCsPeopleAutocompleteQuery = (searchText) => {
  if(searchText) {
    const refinedSearchText = getRefinedSearchText(searchText);
    return {
      "filters": [formCsFilterObject("firstName_raw", "raw", `${refinedSearchText}*`),
      formCsFilterObject("lastName_raw", "raw", `${refinedSearchText}*`),
      formCsFilterObject("userId_raw", "raw", `${refinedSearchText}*`)],
      "operation": "OR",
      "properties": null
    }
  } else {
    return {};
  }
}

/**
 * Function to form search sub-query for matter auto-complete
 * @param {*} searchText
 * @returns search query
 */
export const formCsMatterAutocompleteQuery = (searchText) => {
  if(searchText) {
    const refinedSearchText = getRefinedSearchText(searchText);
    return {
      "filters": [formCsFilterObject("matterName_raw", "raw", `*${refinedSearchText}*`),
      formCsFilterObject("matterNumber", "raw", `*${refinedSearchText}*`)],
      "operation": "OR",
      "properties": null
    }
  } else {
    return {};
  }
}

/**
 * Function to form search sub-query for org auto-complete
 * @param {*} searchText
 * @returns search query
 */
export const formCsOrgAutocompleteQuery = (searchText) => {
  if(searchText) {
    const refinedSearchText = getRefinedSearchText(searchText);
    return {
      "filters": [formCsFilterObject("companyName_raw", "raw", `*${refinedSearchText}*`)],
      "operation": "OR",
      "properties": null
    }
  } else {
    return {};
  }
}

/**
   * Function to form search sub-query for Invoice Line item TK auto-complete
   * @param {*} searchText
   * @returns search query
   */
export const formCsInvoiceTimekeeperAutocompleteQuery = (searchText) => {
  if(searchText)
  {
  return {
    "filters": [formCsFilterObject("firstName_raw", "raw", `${searchText}*`),
    formCsFilterObject("lastName_raw", "raw", `${searchText}*`),
    formCsFilterObject("tkId", "raw", `${searchText}*`)],
    "operation": "OR",
    "properties": null
    }
  }
  else {
    return {};
  }
}

export const getUpdatedFilterQuery = (filterValues) => {
  filterValues?.filters?.forEach((filterType) => {
    if (filterType?.key === 'status') {
      if(Array.isArray(filterType?.value) && filterType?.value?.includes("Ready For Payment") && !filterType?.value?.includes("Hold")) {
        filterType.value = [...filterType?.value, 'Hold']
      }
      if(!Array.isArray(filterType?.value) && filterType?.value === "Ready For Payment") {
        filterType.value = ["Ready For Payment", "Hold"]
        filterType.operation = "in";
      }
    }
  });
  return filterValues;
}
